import NodeDetailManager from '@toruslabs/fetch-node-details';
import { get, setAPIKey } from '@toruslabs/http-helpers';
import { SafeEventEmitter, ObjectMultiplex, createStreamMiddleware, JRPCEngine, createIdRemapMiddleware, BasePostMessageStream, setupMultiplex } from '@toruslabs/openlogin-jrpc';
import TorusJs from '@toruslabs/torus.js';
import deepmerge from 'lodash.merge';
import { ObservableStore, storeAsStream } from '@metamask/obs-store';
import { ethErrors, EthereumRpcError } from 'eth-rpc-errors';
import dequal from 'fast-deep-equal';
import { isDuplexStream } from 'is-stream';
import pump from 'pump';
import loglevel from 'loglevel';
import createHash from 'create-hash';
import { EventEmitter } from 'events';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

const LOGIN_PROVIDER = {
    GOOGLE: "google",
    FACEBOOK: "facebook",
    TWITCH: "twitch",
    REDDIT: "reddit",
    DISCORD: "discord",
};
const WALLET_OPENLOGIN_VERIFIER_MAP = {
    [LOGIN_PROVIDER.GOOGLE]: "tkey-google",
    [LOGIN_PROVIDER.FACEBOOK]: "tkey-facebook",
    [LOGIN_PROVIDER.TWITCH]: "tkey-twitch",
    [LOGIN_PROVIDER.REDDIT]: "tkey-reddit",
    [LOGIN_PROVIDER.DISCORD]: "tkey-discord",
};
const PAYMENT_PROVIDER = {
    MOONPAY: "moonpay",
    WYRE: "wyre",
    RAMPNETWORK: "rampnetwork",
    XANPOOL: "xanpool",
    MERCURYO: "mercuryo",
    TRANSAK: "transak",
};
const TORUS_BUILD_ENV = {
    PRODUCTION: "production",
    DEVELOPMENT: "development",
    BINANCE: "binance",
    TESTING: "testing",
    LRC: "lrc",
    BETA: "beta",
};
const BUTTON_POSITION = {
    BOTTOM_LEFT: "bottom-left",
    TOP_LEFT: "top-left",
    BOTTOM_RIGHT: "bottom-right",
    TOP_RIGHT: "top-right",
};

const paymentProviders$1 = {
    [PAYMENT_PROVIDER.RAMPNETWORK]: {
        line1: "Debit Card/ Apple Pay/ Bank transfer",
        line2: "0.49% - 2.9%",
        line3: "5,000€/purchase, 20,000€/mo",
        supportPage: "https://instant.ramp.network/",
        minOrderValue: 50,
        maxOrderValue: 20000,
        validCurrencies: ["GBP", "EUR", "USD"],
        validCryptoCurrencies: ["ETH", "DAI", "USDC", "BSC_BNB"],
        includeFees: true,
        enforceMax: false,
    },
    [PAYMENT_PROVIDER.MOONPAY]: {
        line1: "Credit / Debit Card / Apple Pay",
        line2: "4.5% or 5 USD",
        line3: "2,000€/day, 10,000€/mo",
        supportPage: "https://help.moonpay.io/en/",
        minOrderValue: 24.99,
        maxOrderValue: 50000,
        validCurrencies: ["USD", "EUR", "GBP", "AUD", "CAD", "SGD", "RUB"],
        validCryptoCurrencies: ["ETH", "DAI", "TUSD", "USDC", "USDT", "BNB_BSC", "BUSD_BSC"],
        includeFees: true,
        enforceMax: false,
    },
    [PAYMENT_PROVIDER.WYRE]: {
        line1: "Apple Pay/ Debit/ Credit Card",
        line2: "4.9% + 30¢ or 5 USD",
        line3: "$250/day",
        supportPage: "https://support.sendwyre.com/en/",
        minOrderValue: 5,
        maxOrderValue: 500,
        validCurrencies: ["USD", "AUD", "CAD", "GBP", "EUR"],
        validCryptoCurrencies: ["ETH", "DAI", "USDC", "USDT"],
        includeFees: false,
        enforceMax: false,
    },
    [PAYMENT_PROVIDER.XANPOOL]: {
        line1: "PayNow/ InstaPay/ FPS/ GoJekPay/ UPI/ PromptPay/ VietelPay/ DuitNow",
        line2: "2.5% buying, 3% selling",
        line3: "$2,500 / day",
        supportPage: "mailto:support@xanpool.com",
        minOrderValue: 100,
        maxOrderValue: 2500,
        validCurrencies: ["SGD", "HKD", "MYR", "PHP", "INR", "VND", "THB", "IDR"],
        validCryptoCurrencies: ["ETH", "USDT"],
        includeFees: true,
        sell: true,
        enforceMax: false,
    },
    [PAYMENT_PROVIDER.MERCURYO]: {
        line1: "Credit/ Debit Card/ Apple Pay",
        line2: "3.95% or 4 USD",
        line3: "10,000€/day, 25,000€/mo",
        supportPage: "mailto:support@mercuryo.io",
        minOrderValue: 30,
        maxOrderValue: 5000,
        validCurrencies: ["USD", "EUR", "RUB", "TRY", "GBP", "UAH"],
        validCryptoCurrencies: ["ETH", "DAI", "BAT", "USDT", "OKB"],
        includeFees: true,
        enforceMax: false,
    },
    [PAYMENT_PROVIDER.TRANSAK]: {
        line1: "Credit/ Debit Card/ <br/>Bank Transfer (sepa/gbp)",
        line2: "0.99% - 5.5% or 5 USD",
        line3: "500€/day",
        supportPage: "https://support.transak.com/hc/en-US",
        minOrderValue: 20,
        maxOrderValue: 500,
        validCurrencies: ["USD", "EUR", "GBP", "AUD", "CAD", "SGD"],
        validCryptoCurrencies: ["ETH", "DAI", "USDC", "USDT"],
        includeFees: true,
        enforceMax: false,
    },
};
const translations = {
    en: {
        embed: {
            continue: "Continue",
            actionRequired: "Authorization required",
            pendingAction: "Click continue to proceed with your request in a popup",
            cookiesRequired: "Cookies Required",
            enableCookies: "Please enable cookies in your browser preferences to access Torus",
            clickHere: "More Info",
        },
    },
    de: {
        embed: {
            continue: "Fortsetzen",
            actionRequired: "Autorisierung erforderlich",
            pendingAction: "Klicken Sie in einem Popup auf Weiter, um mit Ihrer Anfrage fortzufahren",
            cookiesRequired: "Cookies benötigt",
            enableCookies: "Bitte aktivieren Sie Cookies in Ihren Browsereinstellungen, um auf Torus zuzugreifen",
            clickHere: "Mehr Info",
        },
    },
    ja: {
        embed: {
            continue: "継続する",
            actionRequired: "認証が必要です",
            pendingAction: "続行をクリックして、ポップアップでリクエストを続行します",
            cookiesRequired: "必要なクッキー",
            enableCookies: "Torusにアクセスするには、ブラウザの設定でCookieを有効にしてください。",
            clickHere: "詳しくは",
        },
    },
    ko: {
        embed: {
            continue: "계속하다",
            actionRequired: "승인 필요",
            pendingAction: "팝업에서 요청을 진행하려면 계속을 클릭하십시오.",
            cookiesRequired: "쿠키 필요",
            enableCookies: "브라우저 환경 설정에서 쿠키를 활성화하여 Torus에 액세스하십시오.",
            clickHere: "더 많은 정보",
        },
    },
    zh: {
        embed: {
            continue: "继续",
            actionRequired: "需要授权",
            pendingAction: "单击继续以在弹出窗口中继续您的请求",
            cookiesRequired: "必填Cookie",
            enableCookies: "请在您的浏览器首选项中启用cookie以访问Torus。",
            clickHere: "更多信息",
        },
    },
};
var configuration = {
    supportedVerifierList: [LOGIN_PROVIDER.GOOGLE, LOGIN_PROVIDER.REDDIT, LOGIN_PROVIDER.DISCORD],
    paymentProviders: paymentProviders$1,
    api: "https://api.tor.us",
    translations,
    prodTorusUrl: "",
    localStorageKey: `torus-${window.location.hostname}`,
};

const runOnLoad = (fn) => new Promise((resolve, reject) => {
    if (window.document.body != null) {
        Promise.resolve(fn()).then(resolve).catch(reject);
    }
    else {
        window.document.addEventListener("DOMContentLoaded", () => {
            Promise.resolve(fn()).then(resolve).catch(reject);
        });
    }
});
const htmlToElement = (html) => {
    const template = window.document.createElement("template");
    const trimmedHtml = html.trim(); // Never return a text node of whitespace as the result
    template.innerHTML = trimmedHtml;
    return template.content.firstChild;
};
const handleEvent = (handle, eventName, handler, ...handlerArgs) => {
    const handlerWrapper = () => {
        handler(...handlerArgs);
        handle.removeEventListener(eventName, handlerWrapper);
    };
    handle.addEventListener(eventName, handlerWrapper);
};
const handleStream = (handle, eventName, handler) => {
    const handlerWrapper = (chunk) => {
        handler(chunk);
        handle.removeListener(eventName, handlerWrapper);
    };
    handle.on(eventName, handlerWrapper);
};
function documentReady() {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((resolve) => {
            if (document.readyState !== "loading") {
                resolve();
            }
            else {
                handleEvent(document, "DOMContentLoaded", resolve);
            }
        });
    });
}

var log = loglevel.getLogger("torus-embed");

var messages = {
    errors: {
        disconnected: () => "Torus: Lost connection to Torus.",
        permanentlyDisconnected: () => "Torus: Disconnected from iframe. Page reload required.",
        sendSiteMetadata: () => "Torus: Failed to send site metadata. This is an internal error, please report this bug.",
        unsupportedSync: (method) => `Torus: The Torus Ethereum provider does not support synchronous methods like ${method} without a callback parameter.`,
        invalidDuplexStream: () => "Must provide a Node.js-style duplex stream.",
        invalidOptions: (maxEventListeners, shouldSendMetadata) => `Invalid options. Received: { maxEventListeners: ${maxEventListeners}, shouldSendMetadata: ${shouldSendMetadata} }`,
        invalidRequestArgs: () => `Expected a single, non-array, object argument.`,
        invalidRequestMethod: () => `'args.method' must be a non-empty string.`,
        invalidRequestParams: () => `'args.params' must be an object or array if provided.`,
        invalidLoggerObject: () => `'args.logger' must be an object if provided.`,
        invalidLoggerMethod: (method) => `'args.logger' must include required method '${method}'.`,
    },
    info: {
        connected: (chainId) => `Torus: Connected to chain with ID "${chainId}".`,
    },
    warnings: {
        // deprecated methods
        enableDeprecation: 'Torus: ""ethereum.enable()" is deprecated and may be removed in the future. ' +
            'Please use "ethereum.send("eth_requestAccounts")" instead. For more information, see: https://eips.ethereum.org/EIPS/eip-1102',
        sendDeprecation: 'Torus: "ethereum.send(...)" is deprecated and may be removed in the future.' +
            ' Please use "ethereum.sendAsync(...)" or "ethereum.request(...)" instead.\nFor more information, see: https://eips.ethereum.org/EIPS/eip-1193',
        events: {
            close: 'Torus: The event "close" is deprecated and may be removed in the future. Please use "disconnect" instead.' +
                "\nFor more information, see: https://eips.ethereum.org/EIPS/eip-1193",
            data: 'Torus: The event "data" is deprecated and will be removed in the future.' +
                'Use "message" instead.\nFor more information, see: https://eips.ethereum.org/EIPS/eip-1193#message',
            networkChanged: 'Torus: The event "networkChanged" is deprecated and may be removed in the future.' +
                ' Please use "chainChanged" instead.\nFor more information, see: https://eips.ethereum.org/EIPS/eip-1193',
            notification: 'Torus: The event "notification" is deprecated and may be removed in the future. ' +
                'Please use "message" instead.\nFor more information, see: https://eips.ethereum.org/EIPS/eip-1193',
        },
        publicConfigStore: 'Torus: The property "publicConfigStore" is deprecated and WILL be removed in the future.',
    },
};

const { paymentProviders } = configuration;
const validatePaymentProvider = (provider, params) => {
    const errors = {};
    if (!provider) {
        return { errors, isValid: true };
    }
    if (provider && !paymentProviders[provider]) {
        errors.provider = "Invalid Provider";
        return { errors, isValid: Object.keys(errors).length === 0 };
    }
    const selectedProvider = paymentProviders[provider];
    const selectedParams = params || {};
    // set default values
    // if (!selectedParams.selectedCurrency) [selectedParams.selectedCurrency] = selectedProvider.validCurrencies
    // if (!selectedParams.fiatValue) selectedParams.fiatValue = selectedProvider.minOrderValue
    // if (!selectedParams.selectedCryptoCurrency) [selectedParams.selectedCryptoCurrency] = selectedProvider.validCryptoCurrencies
    // validations
    if (selectedParams.fiatValue) {
        const requestedOrderAmount = +parseFloat(selectedParams.fiatValue.toString()) || 0;
        if (requestedOrderAmount < selectedProvider.minOrderValue)
            errors.fiatValue = "Requested amount is lower than supported";
        if (requestedOrderAmount > selectedProvider.maxOrderValue && selectedProvider.enforceMax)
            errors.fiatValue = "Requested amount is higher than supported";
    }
    if (selectedParams.selectedCurrency && !selectedProvider.validCurrencies.includes(selectedParams.selectedCurrency)) {
        errors.selectedCurrency = "Unsupported currency";
    }
    if (selectedParams.selectedCryptoCurrency && !selectedProvider.validCryptoCurrencies.includes(selectedParams.selectedCryptoCurrency)) {
        errors.selectedCryptoCurrency = "Unsupported cryptoCurrency";
    }
    return { errors, isValid: Object.keys(errors).length === 0 };
};
// utility functions
/**
 * json-rpc-engine middleware that logs RPC errors and and validates req.method.
 *
 * @param log - The logging API to use.
 * @returns  json-rpc-engine middleware function
 */
function createErrorMiddleware() {
    return (req, res, next) => {
        // json-rpc-engine will terminate the request when it notices this error
        if (typeof req.method !== "string" || !req.method) {
            res.error = ethErrors.rpc.invalidRequest({
                message: `The request 'method' must be a non-empty string.`,
                data: req,
            });
        }
        next((done) => {
            const { error } = res;
            if (!error) {
                return done();
            }
            log.error(`MetaMask - RPC Error: ${error.message}`, error);
            return done();
        });
    };
}
/**
 * Logs a stream disconnection error. Emits an 'error' if given an
 * EventEmitter that has listeners for the 'error' event.
 *
 * @param log - The logging API to use.
 * @param remoteLabel - The label of the disconnected stream.
 * @param error - The associated error to log.
 * @param emitter - The logging API to use.
 */
function logStreamDisconnectWarning(remoteLabel, error, emitter) {
    let warningMsg = `MetaMask: Lost connection to "${remoteLabel}".`;
    if (error === null || error === void 0 ? void 0 : error.stack) {
        warningMsg += `\n${error.stack}`;
    }
    log.warn(warningMsg);
    if (emitter && emitter.listenerCount("error") > 0) {
        emitter.emit("error", warningMsg);
    }
}
const getPreopenInstanceId = () => Math.random().toString(36).slice(2);
const getTorusUrl = (buildEnv, integrity) => __awaiter(void 0, void 0, void 0, function* () {
    let torusUrl;
    let logLevel;
    // Do not change this line
    const version = "1.18.3";
    let versionUsed = integrity.version || version;
    try {
        if ((buildEnv === "binance" || buildEnv === "production") && !integrity.version) {
            let response;
            if (!configuration.prodTorusUrl)
                response = yield get(`${configuration.api}/latestversion?name=@toruslabs/torus-embed&version=${version}`, {}, { useAPIKey: true });
            else
                response = { data: configuration.prodTorusUrl };
            versionUsed = response.data;
            // eslint-disable-next-line require-atomic-updates
            configuration.prodTorusUrl = response.data;
        }
    }
    catch (error) {
        log.error(error, "unable to fetch latest version");
    }
    log.info("version used: ", versionUsed);
    switch (buildEnv) {
        case "binance":
            torusUrl = `https://binance.tor.us/v${versionUsed}`;
            logLevel = "info";
            break;
        case "testing":
            torusUrl = "https://testing.tor.us";
            logLevel = "debug";
            break;
        case "lrc":
            torusUrl = "https://lrc.tor.us";
            logLevel = "debug";
            break;
        case "beta":
            torusUrl = "https://beta.tor.us";
            logLevel = "debug";
            break;
        case "development":
            torusUrl = "http://localhost:4050";
            logLevel = "debug";
            break;
        default:
            torusUrl = `https://app.tor.us/v${versionUsed}`;
            logLevel = "error";
            break;
    }
    return { torusUrl, logLevel };
});
const getUserLanguage = () => {
    let userLanguage = window.navigator.language || "en-US";
    const userLanguages = userLanguage.split("-");
    userLanguage = Object.prototype.hasOwnProperty.call(configuration.translations, userLanguages[0]) ? userLanguages[0] : "en";
    return userLanguage;
};
const EMITTED_NOTIFICATIONS = [
    "eth_subscription", // per eth-json-rpc-filters/subscriptionManager
];
const NOOP = () => {
    // empty function
};
const FEATURES_PROVIDER_CHANGE_WINDOW = "directories=0,titlebar=0,toolbar=0,status=0,location=0,menubar=0,height=660,width=375";
const FEATURES_DEFAULT_WALLET_WINDOW = "directories=0,titlebar=0,toolbar=0,status=0,location=0,menubar=0,height=740,width=1315";
const FEATURES_CONFIRM_WINDOW = "directories=0,titlebar=0,toolbar=0,status=0,location=0,menubar=0,height=700,width=450";
function storageAvailable(type) {
    let storage;
    try {
        storage = window[type];
        const x = "__storage_test__";
        storage.setItem(x, x);
        storage.removeItem(x);
        return true;
    }
    catch (e) {
        return (e &&
            // everything except Firefox
            (e.code === 22 ||
                // Firefox
                e.code === 1014 ||
                // test name field too, because code might not be present
                // everything except Firefox
                e.name === "QuotaExceededError" ||
                // Firefox
                e.name === "NS_ERROR_DOM_QUOTA_REACHED") &&
            // acknowledge QuotaExceededError only if there's something already stored
            storage &&
            storage.length !== 0);
    }
}
function getPopupFeatures() {
    // Fixes dual-screen position                             Most browsers      Firefox
    const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
    const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;
    const w = 1200;
    const h = 700;
    const width = window.innerWidth
        ? window.innerWidth
        : document.documentElement.clientWidth
            ? document.documentElement.clientWidth
            : window.screen.width;
    const height = window.innerHeight
        ? window.innerHeight
        : document.documentElement.clientHeight
            ? document.documentElement.clientHeight
            : window.screen.height;
    const systemZoom = 1; // No reliable estimate
    const left = Math.abs((width - w) / 2 / systemZoom + dualScreenLeft);
    const top = Math.abs((height - h) / 2 / systemZoom + dualScreenTop);
    const features = `titlebar=0,toolbar=0,status=0,location=0,menubar=0,height=${h / systemZoom},width=${w / systemZoom},top=${top},left=${left}`;
    return features;
}

SafeEventEmitter.defaultMaxListeners = 100;
// resolve response.result, reject errors
const getRpcPromiseCallback = (resolve, reject, unwrapResult = true) => (error, response) => {
    if (error || response.error) {
        return reject(error || response.error);
    }
    return !unwrapResult || Array.isArray(response) ? resolve(response) : resolve(response.result);
};
class TorusInpageProvider extends SafeEventEmitter {
    constructor(connectionStream, { maxEventListeners = 100, shouldSendMetadata = true, jsonRpcStreamName = "provider" } = {}) {
        super();
        this._sentWarnings = {
            // methods
            enable: false,
            experimentalMethods: false,
            send: false,
            publicConfigStore: false,
            // events
            events: {
                close: false,
                data: false,
                networkChanged: false,
                notification: false,
            },
        };
        if (!isDuplexStream(connectionStream)) {
            throw new Error(messages.errors.invalidDuplexStream());
        }
        this.isTorus = true;
        this.setMaxListeners(maxEventListeners);
        // private state
        this._state = Object.assign({}, TorusInpageProvider._defaultState);
        // public state
        this.selectedAddress = null;
        this.networkVersion = null;
        this.chainId = null;
        this.shouldSendMetadata = shouldSendMetadata;
        // bind functions (to prevent e.g. web3@1.x from making unbound calls)
        this._handleAccountsChanged = this._handleAccountsChanged.bind(this);
        this._handleChainChanged = this._handleChainChanged.bind(this);
        this._handleUnlockStateChanged = this._handleUnlockStateChanged.bind(this);
        this._handleConnect = this._handleConnect.bind(this);
        this._handleDisconnect = this._handleDisconnect.bind(this);
        this._handleStreamDisconnect = this._handleStreamDisconnect.bind(this);
        this._sendSync = this._sendSync.bind(this);
        this._rpcRequest = this._rpcRequest.bind(this);
        this._warnOfDeprecation = this._warnOfDeprecation.bind(this);
        this._initializeState = this._initializeState.bind(this);
        this.request = this.request.bind(this);
        this.send = this.send.bind(this);
        this.sendAsync = this.sendAsync.bind(this);
        // this.enable = this.enable.bind(this);
        // setup connectionStream multiplexing
        const mux = new ObjectMultiplex();
        pump(connectionStream, mux, connectionStream, this._handleStreamDisconnect.bind(this, "MetaMask"));
        // subscribe to metamask public config (one-way)
        this._publicConfigStore = new ObservableStore({ storageKey: "Metamask-Config" });
        // handle isUnlocked changes, and chainChanged and networkChanged events
        // this._publicConfigStore.subscribe((stringifiedState) => {
        //   // This is because we are using store as string
        //   const state = JSON.parse(stringifiedState as unknown as string);
        //   if ("isUnlocked" in state && state.isUnlocked !== this._state.isUnlocked) {
        //     this._state.isUnlocked = state.isUnlocked;
        //     if (!this._state.isUnlocked) {
        //       // accounts are never exposed when the extension is locked
        //       this._handleAccountsChanged([]);
        //     } else {
        //       // this will get the exposed accounts, if any
        //       try {
        //         this._rpcRequest(
        //           { method: "eth_accounts", params: [] },
        //           NOOP,
        //           true // indicating that eth_accounts _should_ update accounts
        //         );
        //       } catch (_) {
        //         // Swallow error
        //       }
        //     }
        //   }
        //   if ("selectedAddress" in state && this.selectedAddress !== state.selectedAddress) {
        //     try {
        //       this._rpcRequest(
        //         { method: "eth_accounts", params: [] },
        //         NOOP,
        //         true // indicating that eth_accounts _should_ update accounts
        //       );
        //     } catch (_) {
        //       // Swallow error
        //     }
        //   }
        //   // Emit chainChanged event on chain change
        //   if ("chainId" in state && state.chainId !== this.chainId) {
        //     this.chainId = state.chainId || null;
        //     this.emit("chainChanged", this.chainId);
        //     // indicate that we've connected, for EIP-1193 compliance
        //     // we do this here so that iframe can initialize
        //     if (!this._state.hasEmittedConnection) {
        //       this._handleConnect(this.chainId);
        //       this._state.hasEmittedConnection = true;
        //     }
        //   }
        //   // Emit networkChanged event on network change
        //   if ("networkVersion" in state && state.networkVersion !== this.networkVersion) {
        //     this.networkVersion = state.networkVersion || null;
        //     this.emit("networkChanged", this.networkVersion);
        //   }
        // });
        pump(mux.createStream("publicConfig"), storeAsStream(this._publicConfigStore), 
        // RPC requests should still work if only this stream fails
        logStreamDisconnectWarning.bind(this, "MetaMask PublicConfigStore"));
        // ignore phishing warning message (handled elsewhere)
        mux.ignoreStream("phishing");
        // setup own event listeners
        // EIP-1193 connect
        this.on("connect", () => {
            this._state.isConnected = true;
        });
        // connect to async provider
        const jsonRpcConnection = createStreamMiddleware();
        pump(jsonRpcConnection.stream, mux.createStream(jsonRpcStreamName), jsonRpcConnection.stream, this._handleStreamDisconnect.bind(this, "MetaMask RpcProvider"));
        // handle RPC requests via dapp-side rpc engine
        const rpcEngine = new JRPCEngine();
        rpcEngine.push(createIdRemapMiddleware());
        rpcEngine.push(createErrorMiddleware());
        rpcEngine.push(jsonRpcConnection.middleware);
        this._rpcEngine = rpcEngine;
        // json rpc notification listener
        jsonRpcConnection.events.on("notification", (payload) => {
            const { method, params } = payload;
            if (method === "wallet_accountsChanged") {
                this._handleAccountsChanged(params);
            }
            else if (method === "wallet_unlockStateChanged") {
                this._handleUnlockStateChanged(params);
            }
            else if (method === "wallet_chainChanged") {
                this._handleChainChanged(params);
            }
            else if (EMITTED_NOTIFICATIONS.includes(payload.method)) {
                // EIP 1193 subscriptions, per eth-json-rpc-filters/subscriptionManager
                this.emit("data", payload); // deprecated
                this.emit("notification", params.result);
                this.emit("message", {
                    type: method,
                    data: params,
                });
            }
            // Backward compatibility for older non EIP 1193 subscriptions
            // this.emit('data', null, payload)
        });
    }
    get publicConfigStore() {
        if (!this._sentWarnings.publicConfigStore) {
            log.warn(messages.warnings.publicConfigStore);
            this._sentWarnings.publicConfigStore = true;
        }
        return this._publicConfigStore;
    }
    /**
     * Returns whether the inpage provider is connected to Torus.
     */
    isConnected() {
        return this._state.isConnected;
    }
    /**
     * Submits an RPC request for the given method, with the given params.
     * Resolves with the result of the method call, or rejects on error.
     *
     * @param args - The RPC request arguments.
     * @returns A Promise that resolves with the result of the RPC method,
     * or rejects if an error is encountered.
     */
    request(args) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!args || typeof args !== "object" || Array.isArray(args)) {
                throw ethErrors.rpc.invalidRequest({
                    message: messages.errors.invalidRequestArgs(),
                    data: args,
                });
            }
            const { method, params } = args;
            if (typeof method !== "string" || method.length === 0) {
                throw ethErrors.rpc.invalidRequest({
                    message: messages.errors.invalidRequestMethod(),
                    data: args,
                });
            }
            if (params !== undefined && !Array.isArray(params) && (typeof params !== "object" || params === null)) {
                throw ethErrors.rpc.invalidRequest({
                    message: messages.errors.invalidRequestParams(),
                    data: args,
                });
            }
            return new Promise((resolve, reject) => {
                this._rpcRequest({ method, params }, getRpcPromiseCallback(resolve, reject));
            });
        });
    }
    /**
     * Submits an RPC request per the given JSON-RPC request object.
     *
     * @param payload - The RPC request object.
     * @param cb - The callback function.
     */
    sendAsync(payload, callback) {
        this._rpcRequest(payload, callback);
    }
    /**
     * We override the following event methods so that we can warn consumers
     * about deprecated events:
     *   addListener, on, once, prependListener, prependOnceListener
     */
    addListener(eventName, listener) {
        this._warnOfDeprecation(eventName);
        return super.addListener(eventName, listener);
    }
    on(eventName, listener) {
        this._warnOfDeprecation(eventName);
        return super.on(eventName, listener);
    }
    once(eventName, listener) {
        this._warnOfDeprecation(eventName);
        return super.once(eventName, listener);
    }
    prependListener(eventName, listener) {
        this._warnOfDeprecation(eventName);
        return super.prependListener(eventName, listener);
    }
    prependOnceListener(eventName, listener) {
        this._warnOfDeprecation(eventName);
        return super.prependOnceListener(eventName, listener);
    }
    // Private Methods
    //= ===================
    /**
     * Constructor helper.
     * Populates initial state by calling 'wallet_getProviderState' and emits
     * necessary events.
     */
    _initializeState() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const { accounts, chainId, isUnlocked, networkVersion } = (yield this.request({
                    method: "wallet_getProviderState",
                }));
                // indicate that we've connected, for EIP-1193 compliance
                this.emit("connect", { chainId });
                this._handleChainChanged({ chainId, networkVersion });
                this._handleUnlockStateChanged({ accounts, isUnlocked });
                this._handleAccountsChanged(accounts);
            }
            catch (error) {
                log.error("MetaMask: Failed to get initial state. Please report this bug.", error);
            }
            finally {
                log.info("initialized state");
                this._state.initialized = true;
                this.emit("_initialized");
            }
        });
    }
    /**
     * Internal RPC method. Forwards requests to background via the RPC engine.
     * Also remap ids inbound and outbound.
     *
     * @param payload - The RPC request object.
     * @param callback - The consumer's callback.
     * @param isInternal - false - Whether the request is internal.
     */
    _rpcRequest(payload, callback, isInternal = false) {
        let cb = callback;
        const _payload = payload;
        if (!Array.isArray(_payload)) {
            if (!_payload.jsonrpc) {
                _payload.jsonrpc = "2.0";
            }
            if (_payload.method === "eth_accounts" || _payload.method === "eth_requestAccounts") {
                // handle accounts changing
                cb = (err, res) => {
                    this._handleAccountsChanged(res.result || [], _payload.method === "eth_accounts", isInternal);
                    callback(err, res);
                };
            }
            else if (_payload.method === "wallet_getProviderState") {
                this._rpcEngine.handle(payload, cb);
                return;
            }
        }
        this.tryPreopenHandle(_payload, cb);
    }
    send(methodOrPayload, callbackOrArgs) {
        if (!this._sentWarnings.send) {
            log.warn(messages.warnings.sendDeprecation);
            this._sentWarnings.send = true;
        }
        if (typeof methodOrPayload === "string" && (!callbackOrArgs || Array.isArray(callbackOrArgs))) {
            return new Promise((resolve, reject) => {
                try {
                    this._rpcRequest({ method: methodOrPayload, params: callbackOrArgs }, getRpcPromiseCallback(resolve, reject, false));
                }
                catch (error) {
                    reject(error);
                }
            });
        }
        if (methodOrPayload && typeof methodOrPayload === "object" && typeof callbackOrArgs === "function") {
            return this._rpcRequest(methodOrPayload, callbackOrArgs);
        }
        return this._sendSync(methodOrPayload);
    }
    /**
     * DEPRECATED.
     * Internal backwards compatibility method, used in send.
     */
    _sendSync(payload) {
        let result;
        switch (payload.method) {
            case "eth_accounts":
                result = this.selectedAddress ? [this.selectedAddress] : [];
                break;
            case "eth_coinbase":
                result = this.selectedAddress || null;
                break;
            case "eth_uninstallFilter":
                this._rpcRequest(payload, NOOP);
                result = true;
                break;
            case "net_version":
                result = this.networkVersion || null;
                break;
            default:
                throw new Error(messages.errors.unsupportedSync(payload.method));
        }
        return {
            id: payload.id,
            jsonrpc: payload.jsonrpc,
            result,
        };
    }
    /**
     * When the provider becomes connected, updates internal state and emits
     * required events. Idempotent.
     *
     * @param chainId - The ID of the newly connected chain.
     * emits MetaMaskInpageProvider#connect
     */
    _handleConnect(chainId) {
        if (!this._state.isConnected) {
            this._state.isConnected = true;
            this.emit("connect", { chainId });
            log.debug(messages.info.connected(chainId));
        }
    }
    /**
     * When the provider becomes disconnected, updates internal state and emits
     * required events. Idempotent with respect to the isRecoverable parameter.
     *
     * Error codes per the CloseEvent status codes as required by EIP-1193:
     * https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent#Status_codes
     *
     * @param isRecoverable - Whether the disconnection is recoverable.
     * @param errorMessage - A custom error message.
     * emits MetaMaskInpageProvider#disconnect
     */
    _handleDisconnect(isRecoverable, errorMessage) {
        if (this._state.isConnected || (!this._state.isPermanentlyDisconnected && !isRecoverable)) {
            this._state.isConnected = false;
            let error;
            if (isRecoverable) {
                error = new EthereumRpcError(1013, // Try again later
                errorMessage || messages.errors.disconnected());
                log.debug(error);
            }
            else {
                error = new EthereumRpcError(1011, // Internal error
                errorMessage || messages.errors.permanentlyDisconnected());
                log.error(error);
                this.chainId = null;
                this._state.accounts = null;
                this.selectedAddress = null;
                this._state.isUnlocked = false;
                this._state.isPermanentlyDisconnected = true;
            }
            this.emit("disconnect", error);
        }
    }
    /**
     * Called when connection is lost to critical streams.
     *
     * emits MetamaskInpageProvider#disconnect
     */
    _handleStreamDisconnect(streamName, error) {
        logStreamDisconnectWarning(streamName, error, this);
        this._handleDisconnect(false, error ? error.message : undefined);
    }
    /**
     * Called when accounts may have changed.
     */
    _handleAccountsChanged(accounts, isEthAccounts = false, isInternal = false) {
        // defensive programming
        let finalAccounts = accounts;
        if (!Array.isArray(finalAccounts)) {
            log.error("MetaMask: Received non-array accounts parameter. Please report this bug.", finalAccounts);
            finalAccounts = [];
        }
        for (const account of accounts) {
            if (typeof account !== "string") {
                log.error("MetaMask: Received non-string account. Please report this bug.", accounts);
                finalAccounts = [];
                break;
            }
        }
        // emit accountsChanged if anything about the accounts array has changed
        if (!dequal(this._state.accounts, finalAccounts)) {
            // we should always have the correct accounts even before eth_accounts
            // returns, except in cases where isInternal is true
            if (isEthAccounts && Array.isArray(this._state.accounts) && this._state.accounts.length > 0 && !isInternal) {
                log.error('MetaMask: "eth_accounts" unexpectedly updated accounts. Please report this bug.', finalAccounts);
            }
            this._state.accounts = finalAccounts;
            this.emit("accountsChanged", finalAccounts);
        }
        // handle selectedAddress
        if (this.selectedAddress !== finalAccounts[0]) {
            this.selectedAddress = finalAccounts[0] || null;
        }
    }
    /**
     * Upon receipt of a new chainId and networkVersion, emits corresponding
     * events and sets relevant public state.
     * Does nothing if neither the chainId nor the networkVersion are different
     * from existing values.
     *
     * emits MetamaskInpageProvider#chainChanged
     * @param networkInfo - An object with network info.
     */
    _handleChainChanged({ chainId, networkVersion } = {}) {
        if (!chainId || !networkVersion) {
            log.error("MetaMask: Received invalid network parameters. Please report this bug.", { chainId, networkVersion });
            return;
        }
        if (networkVersion === "loading") {
            this._handleDisconnect(true);
        }
        else {
            this._handleConnect(chainId);
            if (chainId !== this.chainId) {
                this.chainId = chainId;
                if (this._state.initialized) {
                    this.emit("chainChanged", this.chainId);
                }
            }
        }
    }
    /**
     * Upon receipt of a new isUnlocked state, sets relevant public state.
     * Calls the accounts changed handler with the received accounts, or an empty
     * array.
     *
     * Does nothing if the received value is equal to the existing value.
     * There are no lock/unlock events.
     *
     * @param opts - Options bag.
     */
    _handleUnlockStateChanged({ accounts, isUnlocked } = {}) {
        if (typeof isUnlocked !== "boolean") {
            log.error("MetaMask: Received invalid isUnlocked parameter. Please report this bug.", { isUnlocked });
            return;
        }
        if (isUnlocked !== this._state.isUnlocked) {
            this._state.isUnlocked = isUnlocked;
            this._handleAccountsChanged(accounts || []);
        }
    }
    /**
     * Warns of deprecation for the given event, if applicable.
     */
    _warnOfDeprecation(eventName) {
        if (this._sentWarnings.events[eventName] === false) {
            log.warn(messages.warnings.events[eventName]);
            this._sentWarnings.events[eventName] = true;
        }
    }
}
TorusInpageProvider._defaultState = {
    accounts: null,
    isConnected: false,
    isUnlocked: false,
    initialized: false,
    isPermanentlyDisconnected: false,
    hasEmittedConnection: false,
};

const defaults = (options) => ({
    algorithms: options.algorithms || ["sha256"],
    delimiter: options.delimiter || " ",
    full: options.full || false,
});
// Generate list of hashes
const hashes = (options, data) => {
    const internalHashes = {};
    options.algorithms.forEach((algorithm) => {
        internalHashes[algorithm] = createHash(algorithm).update(data, "utf8").digest("base64");
    });
    return internalHashes;
};
// Build an integrity string
const integrity = (options, sri) => {
    let output = "";
    // Hash list
    output += Object.keys(sri.hashes)
        .map((algorithm) => `${algorithm}-${sri.hashes[algorithm]}`)
        .join(options.delimiter);
    return output;
};
const main = (options, data) => {
    // Defaults
    const finalOptions = defaults(options);
    const sri = {
        hashes: hashes(finalOptions, data),
        integrity: undefined,
    };
    sri.integrity = integrity(finalOptions, sri);
    return finalOptions.full ? sri : sri.integrity;
};

class PopupHandler extends EventEmitter {
    constructor({ url, target, features }) {
        super();
        this.url = url;
        this.target = target || "_blank";
        this.features = features || getPopupFeatures();
        this.window = undefined;
        this.windowTimer = undefined;
        this.iClosedWindow = false;
        this._setupTimer();
    }
    _setupTimer() {
        this.windowTimer = Number(setInterval(() => {
            if (this.window && this.window.closed) {
                clearInterval(this.windowTimer);
                if (!this.iClosedWindow) {
                    this.emit("close");
                }
                this.iClosedWindow = false;
                this.window = undefined;
            }
            if (this.window === undefined)
                clearInterval(this.windowTimer);
        }, 500));
    }
    open() {
        var _a;
        this.window = window.open(this.url.href, this.target, this.features);
        if ((_a = this.window) === null || _a === void 0 ? void 0 : _a.focus)
            this.window.focus();
        return Promise.resolve();
    }
    close() {
        this.iClosedWindow = true;
        if (this.window)
            this.window.close();
    }
    redirect(locationReplaceOnRedirect) {
        if (locationReplaceOnRedirect) {
            window.location.replace(this.url.href);
        }
        else {
            window.location.href = this.url.href;
        }
    }
}

/**
 * Returns whether the given image URL exists
 * @param url - the url of the image
 * @returns - whether the image exists
 */
function imgExists(url) {
    return new Promise((resolve, reject) => {
        try {
            const img = document.createElement("img");
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = url;
        }
        catch (e) {
            reject(e);
        }
    });
}
/**
 * Extracts a name for the site from the DOM
 */
const getSiteName = (window) => {
    const { document } = window;
    const siteName = document.querySelector('head > meta[property="og:site_name"]');
    if (siteName) {
        return siteName.content;
    }
    const metaTitle = document.querySelector('head > meta[name="title"]');
    if (metaTitle) {
        return metaTitle.content;
    }
    if (document.title && document.title.length > 0) {
        return document.title;
    }
    return window.location.hostname;
};
/**
 * Extracts an icon for the site from the DOM
 */
function getSiteIcon(window) {
    return __awaiter(this, void 0, void 0, function* () {
        const { document } = window;
        // Use the site's favicon if it exists
        let icon = document.querySelector('head > link[rel="shortcut icon"]');
        if (icon && (yield imgExists(icon.href))) {
            return icon.href;
        }
        // Search through available icons in no particular order
        icon = Array.from(document.querySelectorAll('head > link[rel="icon"]')).find((_icon) => Boolean(_icon.href));
        if (icon && (yield imgExists(icon.href))) {
            return icon.href;
        }
        return null;
    });
}
/**
 * Gets site metadata and returns it
 *
 */
const getSiteMetadata = () => __awaiter(void 0, void 0, void 0, function* () {
    return ({
        name: getSiteName(window),
        icon: yield getSiteIcon(window),
    });
});
/**
 * Sends site metadata over an RPC request.
 */
function sendSiteMetadata(engine) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const domainMetadata = yield getSiteMetadata();
            // call engine.handle directly to avoid normal RPC request handling
            engine.handle({
                jsonrpc: "2.0",
                id: getPreopenInstanceId(),
                method: "wallet_sendDomainMetadata",
                params: domainMetadata,
            }, NOOP);
        }
        catch (error) {
            log.error({
                message: messages.errors.sendSiteMetadata(),
                originalError: error,
            });
        }
    });
}

const defaultVerifiers = {
    [LOGIN_PROVIDER.GOOGLE]: true,
    [LOGIN_PROVIDER.FACEBOOK]: true,
    [LOGIN_PROVIDER.REDDIT]: true,
    [LOGIN_PROVIDER.TWITCH]: true,
    [LOGIN_PROVIDER.DISCORD]: true,
};
const iframeIntegrity = "sha384-LZaLas0G7pCZcRsMwNpCiAndycMHsXIxyiJfNOLU4XZYVj1ablfN8ZEjn/fhI+7A";
const expectedCacheControlHeader = "max-age=3600";
const UNSAFE_METHODS = [
    "eth_sendTransaction",
    "eth_signTypedData",
    "eth_signTypedData_v3",
    "eth_signTypedData_v4",
    "personal_sign",
    "eth_getEncryptionPublicKey",
    "eth_decrypt",
];
const isLocalStorageAvailable = storageAvailable("localStorage");
// preload for iframe doesn't work https://bugs.chromium.org/p/chromium/issues/detail?id=593267
(function preLoadIframe() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            if (typeof document === "undefined")
                return;
            const torusIframeHtml = document.createElement("link");
            const { torusUrl } = yield getTorusUrl("production", { check: false, hash: iframeIntegrity, version: "" });
            torusIframeHtml.href = `${torusUrl}/popup`;
            torusIframeHtml.crossOrigin = "anonymous";
            torusIframeHtml.type = "text/html";
            torusIframeHtml.rel = "prefetch";
            if (torusIframeHtml.relList && torusIframeHtml.relList.supports) {
                if (torusIframeHtml.relList.supports("prefetch")) {
                    document.head.appendChild(torusIframeHtml);
                }
            }
        }
        catch (error) {
            log.warn(error);
        }
    });
})();
class Torus {
    constructor({ buttonPosition = BUTTON_POSITION.BOTTOM_LEFT, modalZIndex = 99999, apiKey = "torus-default" } = {}) {
        this.buttonPosition = BUTTON_POSITION.BOTTOM_LEFT;
        this.paymentProviders = configuration.paymentProviders;
        this.buttonPosition = buttonPosition;
        this.torusUrl = "";
        this.isLoggedIn = false; // ethereum.enable working
        this.isInitialized = false; // init done
        this.torusWidgetVisibility = true;
        this.requestedVerifier = "";
        this.currentVerifier = "";
        this.nodeDetailManager = new NodeDetailManager();
        this.torusJs = new TorusJs({
            metadataHost: "https://metadata.tor.us",
            allowHost: "https://signer.tor.us/api/allow",
        });
        this.apiKey = apiKey;
        TorusJs.setAPIKey(apiKey);
        setAPIKey(apiKey);
        this.modalZIndex = modalZIndex;
        this.alertZIndex = modalZIndex + 1000;
        this.isIframeFullScreen = false;
        this.dappStorageKey = "";
    }
    init({ buildEnv = TORUS_BUILD_ENV.PRODUCTION, enableLogging = false, 
    // deprecated: use loginConfig instead
    enabledVerifiers = defaultVerifiers, network = {
        host: "mainnet",
        chainId: null,
        networkName: "",
        blockExplorer: "",
        ticker: "",
        tickerName: "",
    }, loginConfig = {}, showTorusButton = true, integrity = {
        check: false,
        hash: iframeIntegrity,
        version: "",
    }, whiteLabel, skipTKey = false, useLocalStorage = false, } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isInitialized)
                throw new Error("Already initialized");
            const { torusUrl, logLevel } = yield getTorusUrl(buildEnv, integrity);
            log.info(torusUrl, "url loaded");
            this.torusUrl = torusUrl;
            this.whiteLabel = whiteLabel;
            log.setDefaultLevel(logLevel);
            if (enableLogging)
                log.enableAll();
            else
                log.disableAll();
            this.torusWidgetVisibility = showTorusButton;
            let dappStorageKey = "";
            if (isLocalStorageAvailable && useLocalStorage) {
                const storedKey = window.localStorage.getItem(configuration.localStorageKey);
                if (storedKey)
                    dappStorageKey = storedKey;
                else {
                    const generatedKey = `torus-app-${getPreopenInstanceId()}`;
                    window.localStorage.setItem(configuration.localStorageKey, generatedKey);
                    dappStorageKey = generatedKey;
                }
            }
            this.dappStorageKey = dappStorageKey;
            const torusIframeUrl = new URL(torusUrl);
            if (torusIframeUrl.pathname.endsWith("/"))
                torusIframeUrl.pathname += "popup";
            else
                torusIframeUrl.pathname += "/popup";
            if (dappStorageKey) {
                torusIframeUrl.hash = `#dappStorageKey=${dappStorageKey}`;
            }
            // Iframe code
            this.torusIframe = htmlToElement(`<iframe
        id="torusIframe"
        class="torusIframe"
        src="${torusIframeUrl.href}"
        style="display: none; position: fixed; top: 0; right: 0; width: 100%;
        height: 100%; border: none; border-radius: 0; z-index: ${this.modalZIndex}"
      ></iframe>`);
            this.torusAlertContainer = htmlToElement('<div id="torusAlertContainer"></div>');
            this.torusAlertContainer.style.display = "none";
            this.torusAlertContainer.style.setProperty("z-index", this.alertZIndex.toString());
            const link = window.document.createElement("link");
            link.setAttribute("rel", "stylesheet");
            link.setAttribute("type", "text/css");
            link.setAttribute("href", `${torusUrl}/css/widget.css`);
            this.styleLink = link;
            const { defaultLanguage = getUserLanguage(), customTranslations = {} } = this.whiteLabel || {};
            const mergedTranslations = deepmerge(configuration.translations, customTranslations);
            const languageTranslations = mergedTranslations[defaultLanguage] || configuration.translations[getUserLanguage()];
            this.embedTranslations = languageTranslations.embed;
            const handleSetup = () => __awaiter(this, void 0, void 0, function* () {
                yield documentReady();
                return new Promise((resolve, reject) => {
                    this.torusIframe.onload = () => __awaiter(this, void 0, void 0, function* () {
                        // only do this if iframe is not full screen
                        this._setupWeb3();
                        const initStream = this.communicationMux.getStream("init_stream");
                        initStream.on("data", (chunk) => {
                            const { name, data, error } = chunk;
                            if (name === "init_complete" && data.success) {
                                // resolve promise
                                this.isInitialized = true;
                                this._displayIframe(this.isIframeFullScreen);
                                resolve(undefined);
                            }
                            else if (error) {
                                reject(new Error(error));
                            }
                        });
                        initStream.write({
                            name: "init_stream",
                            data: {
                                enabledVerifiers,
                                loginConfig,
                                whiteLabel: this.whiteLabel,
                                buttonPosition: this.buttonPosition,
                                torusWidgetVisibility: this.torusWidgetVisibility,
                                apiKey: this.apiKey,
                                skipTKey,
                                network,
                            },
                        });
                    });
                    window.document.head.appendChild(this.styleLink);
                    window.document.body.appendChild(this.torusIframe);
                    window.document.body.appendChild(this.torusAlertContainer);
                });
            });
            if (buildEnv === "production" && integrity.check) {
                // hacky solution to check for iframe integrity
                const fetchUrl = `${torusUrl}/popup`;
                const resp = yield fetch(fetchUrl, { cache: "reload" });
                if (resp.headers.get("Cache-Control") !== expectedCacheControlHeader) {
                    throw new Error(`Unexpected Cache-Control headers, got ${resp.headers.get("Cache-Control")}`);
                }
                const response = yield resp.text();
                const calculatedIntegrity = main({
                    algorithms: ["sha384"],
                }, response);
                log.info(calculatedIntegrity, "integrity");
                if (calculatedIntegrity === integrity.hash) {
                    yield handleSetup();
                }
                else {
                    this.clearInit();
                    throw new Error("Integrity check failed");
                }
            }
            else {
                yield handleSetup();
            }
            return undefined;
        });
    }
    login({ verifier = "" } = {}) {
        if (!this.isInitialized)
            throw new Error("Call init() first");
        this.requestedVerifier = verifier;
        return this.ethereum.enable();
    }
    logout() {
        return new Promise((resolve, reject) => {
            if (!this.isLoggedIn) {
                reject(new Error("User has not logged in yet"));
                return;
            }
            const logOutStream = this.communicationMux.getStream("logout");
            logOutStream.write({ name: "logOut" });
            const statusStream = this.communicationMux.getStream("status");
            const statusStreamHandler = (status) => {
                if (!status.loggedIn) {
                    this.isLoggedIn = false;
                    this.currentVerifier = "";
                    this.requestedVerifier = "";
                    resolve();
                }
                else
                    reject(new Error("Some Error Occured"));
            };
            handleStream(statusStream, "data", statusStreamHandler);
        });
    }
    cleanUp() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isLoggedIn) {
                yield this.logout();
            }
            this.clearInit();
        });
    }
    clearInit() {
        function isElement(element) {
            return element instanceof Element || element instanceof HTMLDocument;
        }
        if (isElement(this.styleLink) && window.document.body.contains(this.styleLink)) {
            this.styleLink.remove();
            this.styleLink = undefined;
        }
        if (isElement(this.torusIframe) && window.document.body.contains(this.torusIframe)) {
            this.torusIframe.remove();
            this.torusIframe = undefined;
        }
        if (isElement(this.torusAlertContainer) && window.document.body.contains(this.torusAlertContainer)) {
            this.torusAlert = undefined;
            this.torusAlertContainer.remove();
            this.torusAlertContainer = undefined;
        }
        this.isInitialized = false;
    }
    hideTorusButton() {
        this.torusWidgetVisibility = false;
        this._sendWidgetVisibilityStatus(false);
        this._displayIframe();
    }
    showTorusButton() {
        this.torusWidgetVisibility = true;
        this._sendWidgetVisibilityStatus(true);
        this._displayIframe();
    }
    setProvider(_a = {}) {
        var { host = "mainnet", chainId = null, networkName = "" } = _a, rest = __rest(_a, ["host", "chainId", "networkName"]);
        return new Promise((resolve, reject) => {
            const providerChangeStream = this.communicationMux.getStream("provider_change");
            const handler = (chunk) => {
                const { err, success } = chunk.data;
                log.info(chunk);
                if (err) {
                    reject(err);
                }
                else if (success) {
                    resolve();
                }
                else
                    reject(new Error("some error occured"));
            };
            handleStream(providerChangeStream, "data", handler);
            const preopenInstanceId = getPreopenInstanceId();
            this._handleWindow(preopenInstanceId, {
                target: "_blank",
                features: FEATURES_PROVIDER_CHANGE_WINDOW,
            });
            providerChangeStream.write({
                name: "show_provider_change",
                data: {
                    network: Object.assign({ host,
                        chainId,
                        networkName }, rest),
                    preopenInstanceId,
                    override: false,
                },
            });
        });
    }
    showWallet(path, params = {}) {
        const showWalletStream = this.communicationMux.getStream("show_wallet");
        const finalPath = path ? `/${path}` : "";
        showWalletStream.write({ name: "show_wallet", data: { path: finalPath } });
        const showWalletHandler = (chunk) => {
            if (chunk.name === "show_wallet_instance") {
                // Let the error propogate up (hence, no try catch)
                const { instanceId } = chunk.data;
                const finalUrl = new URL(`${this.torusUrl}/wallet${finalPath}`);
                // Using URL constructor to prevent js injection and allow parameter validation.!
                finalUrl.searchParams.append("integrity", "true");
                finalUrl.searchParams.append("instanceId", instanceId);
                Object.keys(params).forEach((x) => {
                    finalUrl.searchParams.append(x, params[x]);
                });
                if (this.dappStorageKey) {
                    finalUrl.hash = `#dappStorageKey=${this.dappStorageKey}`;
                }
                const walletWindow = new PopupHandler({ url: finalUrl, features: FEATURES_DEFAULT_WALLET_WINDOW });
                walletWindow.open();
            }
        };
        handleStream(showWalletStream, "data", showWalletHandler);
    }
    getPublicAddress({ verifier, verifierId, isExtended = false }) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!configuration.supportedVerifierList.includes(verifier) || !WALLET_OPENLOGIN_VERIFIER_MAP[verifier])
                throw new Error("Unsupported verifier");
            const nodeDetails = yield this.nodeDetailManager.getNodeDetails(false, true);
            const endpoints = nodeDetails.torusNodeEndpoints;
            const torusNodePubs = nodeDetails.torusNodePub;
            const walletVerifier = verifier;
            const openloginVerifier = WALLET_OPENLOGIN_VERIFIER_MAP[verifier];
            try {
                const existingV1User = yield this.torusJs.getUserTypeAndAddress(endpoints, torusNodePubs, { verifier: walletVerifier, verifierId });
                if (existingV1User.typeOfUser === "v1") {
                    if (!isExtended)
                        return existingV1User.address;
                    return existingV1User;
                }
                // we don't support v2 users with v1 verifiers so get or assign the key for v2 user on v2 `verifier`
                const v2User = yield this.torusJs.getUserTypeAndAddress(endpoints, torusNodePubs, { verifier: openloginVerifier, verifierId }, true);
                if (!isExtended)
                    return v2User.address;
                return v2User;
            }
            catch (error) {
                if (error === null || error === void 0 ? void 0 : error.message.includes("Verifier + VerifierID has not yet been assigned")) {
                    // if user doesn't have key then assign it with v2 verifier
                    const newV2User = yield this.torusJs.getUserTypeAndAddress(endpoints, torusNodePubs, { verifier: openloginVerifier, verifierId }, true);
                    if (!isExtended)
                        return newV2User.address;
                    return newV2User;
                }
                throw error;
            }
        });
    }
    getUserInfo(message) {
        return new Promise((resolve, reject) => {
            if (this.isLoggedIn) {
                const userInfoAccessStream = this.communicationMux.getStream("user_info_access");
                userInfoAccessStream.write({ name: "user_info_access_request" });
                const userInfoAccessHandler = (chunk) => {
                    const { name, data: { approved, payload, rejected, newRequest }, } = chunk;
                    if (name === "user_info_access_response") {
                        if (approved) {
                            resolve(payload);
                        }
                        else if (rejected) {
                            reject(new Error("User rejected the request"));
                        }
                        else if (newRequest) {
                            const userInfoStream = this.communicationMux.getStream("user_info");
                            const userInfoHandler = (handlerChunk) => {
                                if (handlerChunk.name === "user_info_response") {
                                    if (handlerChunk.data.approved) {
                                        resolve(handlerChunk.data.payload);
                                    }
                                    else {
                                        reject(new Error("User rejected the request"));
                                    }
                                }
                            };
                            handleStream(userInfoStream, "data", userInfoHandler);
                            const preopenInstanceId = getPreopenInstanceId();
                            this._handleWindow(preopenInstanceId, {
                                target: "_blank",
                                features: FEATURES_PROVIDER_CHANGE_WINDOW,
                            });
                            userInfoStream.write({ name: "user_info_request", data: { message, preopenInstanceId } });
                        }
                    }
                };
                handleStream(userInfoAccessStream, "data", userInfoAccessHandler);
            }
            else
                reject(new Error("User has not logged in yet"));
        });
    }
    initiateTopup(provider, params) {
        return new Promise((resolve, reject) => {
            if (this.isInitialized) {
                const { errors, isValid } = validatePaymentProvider(provider, params);
                if (!isValid) {
                    reject(new Error(JSON.stringify(errors)));
                    return;
                }
                const topupStream = this.communicationMux.getStream("topup");
                const topupHandler = (chunk) => {
                    if (chunk.name === "topup_response") {
                        if (chunk.data.success) {
                            resolve(chunk.data.success);
                        }
                        else {
                            reject(new Error(chunk.data.error));
                        }
                    }
                };
                handleStream(topupStream, "data", topupHandler);
                const preopenInstanceId = getPreopenInstanceId();
                this._handleWindow(preopenInstanceId);
                topupStream.write({ name: "topup_request", data: { provider, params, preopenInstanceId } });
            }
            else
                reject(new Error("Torus is not initialized yet"));
        });
    }
    _handleWindow(preopenInstanceId, { url, target, features } = {}) {
        if (preopenInstanceId) {
            const windowStream = this.communicationMux.getStream("window");
            const finalUrl = new URL(url || `${this.torusUrl}/redirect?preopenInstanceId=${preopenInstanceId}`);
            if (this.dappStorageKey) {
                // If multiple instances, it returns the first one
                if (finalUrl.hash)
                    finalUrl.hash += `&dappStorageKey=${this.dappStorageKey}`;
                else
                    finalUrl.hash = `#dappStorageKey=${this.dappStorageKey}`;
            }
            const handledWindow = new PopupHandler({ url: finalUrl, target, features });
            handledWindow.open();
            if (!handledWindow.window) {
                this._createPopupBlockAlert(preopenInstanceId, finalUrl.href);
                return;
            }
            windowStream.write({
                name: "opened_window",
                data: {
                    preopenInstanceId,
                },
            });
            const closeHandler = ({ preopenInstanceId: receivedId, close }) => {
                if (receivedId === preopenInstanceId && close) {
                    handledWindow.close();
                    windowStream.removeListener("data", closeHandler);
                }
            };
            windowStream.on("data", closeHandler);
            handledWindow.once("close", () => {
                windowStream.write({
                    data: {
                        preopenInstanceId,
                        closed: true,
                    },
                });
                windowStream.removeListener("data", closeHandler);
            });
        }
    }
    _setEmbedWhiteLabel(element) {
        // Set whitelabel
        const { theme } = this.whiteLabel || {};
        if (theme) {
            const { isDark = false, colors = {} } = theme;
            if (isDark)
                element.classList.add("torus-dark");
            if (colors.torusBrand1)
                element.style.setProperty("--torus-brand-1", colors.torusBrand1);
            if (colors.torusGray2)
                element.style.setProperty("--torus-gray-2", colors.torusGray2);
        }
    }
    _getLogoUrl() {
        var _a, _b, _c, _d;
        let logoUrl = `${this.torusUrl}/images/torus_icon-blue.svg`;
        if ((_b = (_a = this.whiteLabel) === null || _a === void 0 ? void 0 : _a.theme) === null || _b === void 0 ? void 0 : _b.isDark) {
            logoUrl = ((_c = this.whiteLabel) === null || _c === void 0 ? void 0 : _c.logoLight) || logoUrl;
        }
        else {
            logoUrl = ((_d = this.whiteLabel) === null || _d === void 0 ? void 0 : _d.logoDark) || logoUrl;
        }
        return logoUrl;
    }
    _sendWidgetVisibilityStatus(status) {
        const torusWidgetVisibilityStream = this.communicationMux.getStream("torus-widget-visibility");
        torusWidgetVisibilityStream.write({
            data: status,
        });
    }
    _displayIframe(isFull = false) {
        const style = {};
        // set phase
        if (!isFull) {
            style.display = this.torusWidgetVisibility ? "block" : "none";
            style.height = "70px";
            style.width = "70px";
            switch (this.buttonPosition) {
                case BUTTON_POSITION.TOP_LEFT:
                    style.top = "0px";
                    style.left = "0px";
                    style.right = "auto";
                    style.bottom = "auto";
                    break;
                case BUTTON_POSITION.TOP_RIGHT:
                    style.top = "0px";
                    style.right = "0px";
                    style.left = "auto";
                    style.bottom = "auto";
                    break;
                case BUTTON_POSITION.BOTTOM_RIGHT:
                    style.bottom = "0px";
                    style.right = "0px";
                    style.top = "auto";
                    style.left = "auto";
                    break;
                case BUTTON_POSITION.BOTTOM_LEFT:
                default:
                    style.bottom = "0px";
                    style.left = "0px";
                    style.top = "auto";
                    style.right = "auto";
                    break;
            }
        }
        else {
            style.display = "block";
            style.width = "100%";
            style.height = "100%";
            style.top = "0px";
            style.right = "0px";
            style.left = "0px";
            style.bottom = "0px";
        }
        Object.assign(this.torusIframe.style, style);
        this.isIframeFullScreen = isFull;
    }
    _setupWeb3() {
        log.info("setupWeb3 running");
        // setup background connection
        const metamaskStream = new BasePostMessageStream({
            name: "embed_metamask",
            target: "iframe_metamask",
            targetWindow: this.torusIframe.contentWindow,
            targetOrigin: new URL(this.torusUrl).origin,
        });
        // Due to compatibility reasons, we should not set up multiplexing on window.metamaskstream
        // because the MetamaskInpageProvider also attempts to do so.
        // We create another LocalMessageDuplexStream for communication between dapp <> iframe
        const communicationStream = new BasePostMessageStream({
            name: "embed_comm",
            target: "iframe_comm",
            targetWindow: this.torusIframe.contentWindow,
            targetOrigin: new URL(this.torusUrl).origin,
        });
        // Backward compatibility with Gotchi :)
        // window.metamaskStream = this.communicationStream
        // compose the inpage provider
        const inpageProvider = new TorusInpageProvider(metamaskStream);
        // detect eth_requestAccounts and pipe to enable for now
        const detectAccountRequestPrototypeModifier = (m) => {
            const originalMethod = inpageProvider[m];
            inpageProvider[m] = function providerFunc(method, ...args) {
                if (method && method === "eth_requestAccounts") {
                    return inpageProvider.enable();
                }
                return originalMethod.apply(this, [method, ...args]);
            };
        };
        detectAccountRequestPrototypeModifier("send");
        detectAccountRequestPrototypeModifier("sendAsync");
        inpageProvider.enable = () => {
            return new Promise((resolve, reject) => {
                // If user is already logged in, we assume they have given access to the website
                inpageProvider.sendAsync({ jsonrpc: "2.0", id: getPreopenInstanceId(), method: "eth_requestAccounts", params: [] }, (err, response) => {
                    const { result: res } = response || {};
                    if (err) {
                        setTimeout(() => {
                            reject(err);
                        }, 50);
                    }
                    else if (Array.isArray(res) && res.length > 0) {
                        // If user is already rehydrated, resolve this
                        // else wait for something to be written to status stream
                        const handleLoginCb = () => {
                            if (this.requestedVerifier !== "" && this.currentVerifier !== this.requestedVerifier) {
                                const { requestedVerifier } = this;
                                // eslint-disable-next-line promise/no-promise-in-callback
                                this.logout()
                                    // eslint-disable-next-line promise/always-return
                                    .then((_) => {
                                    this.requestedVerifier = requestedVerifier;
                                    this._showLoginPopup(true, resolve, reject);
                                })
                                    .catch((error) => reject(error));
                            }
                            else {
                                resolve(res);
                            }
                        };
                        if (this.isLoggedIn) {
                            handleLoginCb();
                        }
                        else {
                            this.isLoginCallback = handleLoginCb;
                        }
                    }
                    else {
                        // set up listener for login
                        this._showLoginPopup(true, resolve, reject);
                    }
                });
            });
        };
        inpageProvider.tryPreopenHandle = (payload, cb) => {
            const _payload = payload;
            if (!Array.isArray(_payload) && UNSAFE_METHODS.includes(_payload.method)) {
                const preopenInstanceId = getPreopenInstanceId();
                this._handleWindow(preopenInstanceId, {
                    target: "_blank",
                    features: FEATURES_CONFIRM_WINDOW,
                });
                _payload.preopenInstanceId = preopenInstanceId;
            }
            inpageProvider._rpcEngine.handle(_payload, cb);
        };
        // Work around for web3@1.0 deleting the bound `sendAsync` but not the unbound
        // `sendAsync` method on the prototype, causing `this` reference issues with drizzle
        const proxiedInpageProvider = new Proxy(inpageProvider, {
            // straight up lie that we deleted the property so that it doesnt
            // throw an error in strict mode
            deleteProperty: () => true,
        });
        this.ethereum = proxiedInpageProvider;
        const communicationMux = setupMultiplex(communicationStream);
        this.communicationMux = communicationMux;
        const windowStream = communicationMux.getStream("window");
        windowStream.on("data", (chunk) => {
            if (chunk.name === "create_window") {
                // url is the url we need to open
                // we can pass the final url upfront so that it removes the step of redirecting to /redirect and waiting for finalUrl
                this._createPopupBlockAlert(chunk.data.preopenInstanceId, chunk.data.url);
            }
        });
        // show torus widget if button clicked
        const widgetStream = communicationMux.getStream("widget");
        widgetStream.on("data", (chunk) => {
            const { data } = chunk;
            this._displayIframe(data);
        });
        // Show torus button if wallet has been hydrated/detected
        const statusStream = communicationMux.getStream("status");
        statusStream.on("data", (status) => {
            // login
            if (status.loggedIn) {
                this.isLoggedIn = status.loggedIn;
                this.currentVerifier = status.verifier;
            } // logout
            else
                this._displayIframe();
            if (this.isLoginCallback) {
                this.isLoginCallback();
                delete this.isLoginCallback;
            }
        });
        this.provider = proxiedInpageProvider;
        if (this.provider.shouldSendMetadata)
            sendSiteMetadata(this.provider._rpcEngine);
        inpageProvider._initializeState();
        log.debug("Torus - injected provider");
    }
    _showLoginPopup(calledFromEmbed, resolve, reject) {
        const loginHandler = (data) => {
            const { err, selectedAddress } = data;
            if (err) {
                log.error(err);
                if (reject)
                    reject(err);
            }
            // returns an array (cause accounts expects it)
            else if (resolve)
                resolve([selectedAddress]);
            if (this.isIframeFullScreen)
                this._displayIframe();
        };
        const oauthStream = this.communicationMux.getStream("oauth");
        if (!this.requestedVerifier) {
            this._displayIframe(true);
            handleStream(oauthStream, "data", loginHandler);
            oauthStream.write({ name: "oauth_modal", data: { calledFromEmbed } });
        }
        else {
            handleStream(oauthStream, "data", loginHandler);
            const preopenInstanceId = getPreopenInstanceId();
            this._handleWindow(preopenInstanceId);
            oauthStream.write({ name: "oauth", data: { calledFromEmbed, verifier: this.requestedVerifier, preopenInstanceId } });
        }
    }
    _createPopupBlockAlert(preopenInstanceId, url) {
        const logoUrl = this._getLogoUrl();
        const torusAlert = htmlToElement('<div id="torusAlert" class="torus-alert--v2">' +
            `<div id="torusAlert__logo"><img src="${logoUrl}" /></div>` +
            "<div>" +
            `<h1 id="torusAlert__title">${this.embedTranslations.actionRequired}</h1>` +
            `<p id="torusAlert__desc">${this.embedTranslations.pendingAction}</p>` +
            "</div>" +
            "</div>");
        const successAlert = htmlToElement(`<div><a id="torusAlert__btn">${this.embedTranslations.continue}</a></div>`);
        const btnContainer = htmlToElement('<div id="torusAlert__btn-container"></div>');
        btnContainer.appendChild(successAlert);
        torusAlert.appendChild(btnContainer);
        const bindOnLoad = () => {
            successAlert.addEventListener("click", () => {
                this._handleWindow(preopenInstanceId, {
                    url,
                    target: "_blank",
                    features: FEATURES_CONFIRM_WINDOW,
                });
                torusAlert.remove();
                if (this.torusAlertContainer.children.length === 0)
                    this.torusAlertContainer.style.display = "none";
            });
        };
        this._setEmbedWhiteLabel(torusAlert);
        const attachOnLoad = () => {
            this.torusAlertContainer.style.display = "block";
            this.torusAlertContainer.appendChild(torusAlert);
        };
        runOnLoad(attachOnLoad);
        runOnLoad(bindOnLoad);
    }
}

export { BUTTON_POSITION, LOGIN_PROVIDER, PAYMENT_PROVIDER, TORUS_BUILD_ENV, TorusInpageProvider, WALLET_OPENLOGIN_VERIFIER_MAP, Torus as default };
//# sourceMappingURL=torus.esm.js.map
